import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Link, Check, Loader2, RefreshCw, Search, Database } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { toast } from 'sonner';

const BatchRelinkDialog = ({ open, onOpenChange, remitentes, destinatarios, onComplete }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedCustomers, setSelectedCustomers] = useState({});
  const [saving, setSaving] = useState({});

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/shipments/diagnostico/orphan-groups');
      setGroups(res.data.groups || []);
    } catch {
      toast.error('Error al cargar grupos huerfanos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setRecoveryResult(null);
      fetchGroups();
    }
  }, [open, fetchGroups]);

  const handleAutoRecover = async () => {
    setRecovering(true);
    setRecoveryResult(null);
    try {
      // First try: recover from old app's tracking API
      const res = await api.post('/shipments/diagnostico/recover-from-old-app', {
        old_app_url: 'https://shipping-config-tool.emergent.host'
      });
      setRecoveryResult(res.data);
      const totalFixed = (res.data.fixed_remitentes || 0) + (res.data.fixed_destinatarios || 0);
      if (totalFixed > 0) {
        toast.success(`${totalFixed} clientes recuperados de la app anterior`);
        fetchGroups();
        if (onComplete) onComplete();
      } else {
        toast.info('No se pudieron recuperar clientes automaticamente');
      }
    } catch (error) {
      toast.error('Error al conectar con la app anterior');
    } finally {
      setRecovering(false);
    }
  };

  const getFilteredCustomers = (groupId, type) => {
    const term = (searchTerms[groupId] || '').toLowerCase();
    const list = type === 'remitente' ? remitentes : destinatarios;
    if (!term) return list.slice(0, 8);
    return list.filter(c =>
      (c.nombre_completo || '').toLowerCase().includes(term) ||
      (c.cedula_pasaporte || '').toLowerCase().includes(term) ||
      (c.telefono || '').toLowerCase().includes(term)
    ).slice(0, 8);
  };

  const handleBatchRelink = async (group) => {
    const customerId = selectedCustomers[group.orphan_id];
    if (!customerId) {
      toast.error('Selecciona un cliente primero');
      return;
    }
    setSaving(prev => ({ ...prev, [group.orphan_id]: true }));
    try {
      const res = await api.post('/shipments/diagnostico/batch-relink', {
        orphan_id: group.orphan_id,
        new_customer_id: customerId,
        type: group.type
      });
      toast.success(`${res.data.customer_name}: ${res.data.updated_count} envios actualizados`);
      // Remove this group from the list
      setGroups(prev => prev.filter(g => g.orphan_id !== group.orphan_id));
      if (groups.length <= 1 && onComplete) onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al re-enlazar');
    } finally {
      setSaving(prev => ({ ...prev, [group.orphan_id]: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="batch-relink-dialog" aria-describedby="batch-relink-desc">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Link className="h-5 w-5 text-blue-500" />
            Re-enlazar Clientes ({groups.length} pendientes)
          </DialogTitle>
          <p id="batch-relink-desc" className="text-sm text-gray-500">
            Estos envios tienen clientes del sistema anterior. Selecciona el cliente correcto para cada grupo.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-8">
            <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-green-700">Todos los envios estan enlazados correctamente</p>
            {recoveryResult && recoveryResult.recovered > 0 && (
              <p className="text-sm text-gray-500 mt-2">Se recuperaron {recoveryResult.recovered} clientes de la base de datos antigua</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Auto-recover button */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium mb-2">
                <Database className="inline h-4 w-4 mr-1" />
                Primero, intenta recuperar los nombres automaticamente de la base de datos del sistema anterior:
              </p>
              <Button
                data-testid="auto-recover-btn"
                onClick={handleAutoRecover}
                disabled={recovering}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700"
              >
                {recovering ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Buscando en todas las bases de datos...</>
                ) : (
                  <><Database className="mr-2 h-4 w-4" /> Recuperar Clientes Automaticamente</>
                )}
              </Button>
              {recoveryResult && (
                <div className={`mt-2 p-2 rounded text-sm ${(recoveryResult.fixed_remitentes > 0 || recoveryResult.fixed_destinatarios > 0) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {(recoveryResult.fixed_remitentes > 0 || recoveryResult.fixed_destinatarios > 0)
                    ? `Recuperados: ${recoveryResult.fixed_remitentes || 0} remitentes, ${recoveryResult.fixed_destinatarios || 0} destinatarios.`
                    : 'No se encontraron clientes en la app anterior.'
                  }
                  {recoveryResult.details?.map((d, i) => (
                    <div key={i} className="text-xs mt-1 font-medium">+ {d}</div>
                  ))}
                  {recoveryResult.errors?.length > 0 && recoveryResult.errors.map((e, i) => (
                    <div key={`e${i}`} className="text-xs mt-1 text-red-600">! {e}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Manual relink section */}
            {groups.length > 0 && (
              <p className="text-sm text-gray-500 font-medium border-t pt-3">Si la recuperacion automatica no resuelve todo, selecciona manualmente:</p>
            )}
            {groups.map((group) => (
              <div key={group.orphan_id} className="border rounded-lg overflow-hidden" data-testid={`orphan-group-${group.orphan_id}`}>
                {/* Header */}
                <div className={`px-4 py-2.5 ${group.type === 'remitente' ? 'bg-blue-50 border-b border-blue-200' : 'bg-green-50 border-b border-green-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className={`text-sm font-bold uppercase ${group.type === 'remitente' ? 'text-blue-700' : 'text-green-700'}`}>
                        {group.type}
                      </span>
                      <span className="text-xs text-gray-500">ID: {group.orphan_id.slice(0, 8)}...</span>
                    </div>
                    <span className="text-xs bg-white border rounded-full px-2 py-0.5 font-medium">
                      {group.facturas.length} factura{group.facturas.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {group.facturas.map(f => (
                      <span key={f} className="text-xs bg-white border rounded px-1.5 py-0.5 font-mono">{f}</span>
                    ))}
                  </div>
                </div>

                {/* Customer search */}
                <div className="p-3 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      data-testid={`search-${group.orphan_id}`}
                      placeholder="Buscar por nombre, cedula o telefono..."
                      className="pl-9 h-9"
                      value={searchTerms[group.orphan_id] || ''}
                      onChange={(e) => setSearchTerms(prev => ({ ...prev, [group.orphan_id]: e.target.value }))}
                    />
                  </div>

                  {/* Customer options */}
                  <div className="max-h-32 overflow-y-auto border rounded bg-gray-50">
                    {getFilteredCustomers(group.orphan_id, group.type).map(customer => (
                      <button
                        key={customer.id}
                        data-testid={`select-customer-${customer.id}`}
                        onClick={() => setSelectedCustomers(prev => ({ ...prev, [group.orphan_id]: customer.id }))}
                        className={`w-full text-left px-3 py-1.5 text-sm border-b last:border-b-0 flex items-center justify-between transition-colors ${
                          selectedCustomers[group.orphan_id] === customer.id
                            ? 'bg-blue-100 text-blue-900 font-semibold'
                            : 'hover:bg-white'
                        }`}
                      >
                        <div>
                          <span className="font-medium">{customer.nombre_completo}</span>
                          {customer.telefono && <span className="text-gray-500 ml-2 text-xs">{customer.telefono}</span>}
                        </div>
                        {selectedCustomers[group.orphan_id] === customer.id && (
                          <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Apply button */}
                  <Button
                    data-testid={`apply-relink-${group.orphan_id}`}
                    onClick={() => handleBatchRelink(group)}
                    disabled={!selectedCustomers[group.orphan_id] || saving[group.orphan_id]}
                    className="w-full h-9 text-sm"
                    variant={selectedCustomers[group.orphan_id] ? "default" : "outline"}
                  >
                    {saving[group.orphan_id] ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
                    ) : (
                      <><Link className="mr-2 h-4 w-4" /> Aplicar a {group.facturas.length} factura{group.facturas.length > 1 ? 's' : ''}</>
                    )}
                  </Button>
                </div>
              </div>
            ))}
            {groups.length > 0 && <div/>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchRelinkDialog;
